import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useLangMap } from '../../hooks/useLanguages';
import { loadGrammarManifest, loadGrammarSection } from '../../services/grammarV2';

// Builds a Sentence Builder question pool from one grammar chapter's own
// example sentences — mirrors the mobile app's grammar_screen.dart: split
// each example into words, keep only sentences short enough to reassemble
// (3-8 words).
async function buildSentencePool(targetCode, chapter) {
  const doc = await loadGrammarSection(targetCode, chapter.id);
  const sections = (doc?.sections || []).filter((s) => s.id === chapter.id);
  const pool = [];
  for (const section of sections) {
    for (const example of section.examples || []) {
      const text = (example?.text || '').trim();
      if (!text) continue;
      const words = text.split(/\s+/).filter(Boolean);
      if (words.length >= 3 && words.length <= 8) {
        pool.push({ id: example.id || text, words });
      }
    }
  }
  return pool;
}

function useGrammarCurriculum(langCode) {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!langCode) return;
    let active = true;
    setLoading(true);
    setError(false);
    setManifest(null);
    loadGrammarManifest(langCode)
      .then((data) => active && setManifest(data))
      .catch(() => active && setError(true))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [langCode]);

  return { manifest, loading, error };
}

function LessonCard({ lesson, onOpen, onPlaySentenceBuilder, sentenceBuilderLabel }) {
  return (
    <div className="w-full rounded-2xl border border-slate-200 bg-white p-4 transition hover:border-violet-200 hover:bg-violet-50/40">
      <button type="button" onClick={onOpen} className="flex w-full items-center justify-between gap-3 text-left">
        <p className="font-black text-slate-900">{lesson.label || lesson.id.replaceAll('_', ' ')}</p>
        <span className="text-slate-300">›</span>
      </button>
      <button
        type="button"
        onClick={onPlaySentenceBuilder}
        className="mt-2.5 flex items-center gap-1.5 rounded-lg bg-indigo-50 px-2.5 py-1.5 text-xs font-bold text-indigo-600 transition hover:bg-indigo-100"
      >
        🧩 {sentenceBuilderLabel}
      </button>
    </div>
  );
}

export default function GrammarPage() {
  const { t } = useTranslation();
  const { languagePrefs } = useAuth();
  const { langMap } = useLangMap();
  const navigate = useNavigate();
  const langCode = languagePrefs?.targetLanguage;
  const lang = langCode ? langMap[langCode] : null;
  const { manifest, loading, error } = useGrammarCurriculum(langCode);
  const chapters = manifest || [];
  const nativeCode = languagePrefs?.nativeLanguage || 'en';

  async function playSentenceBuilder(chapter) {
    try {
      const pool = await buildSentencePool(langCode, chapter);
      if (pool.length === 0) return;
      navigate('/games/sentence-builder', {
        state: {
          pool,
          targetLang: { code: langCode, label: lang?.label, dir: lang?.dir || 'ltr' },
          langPair: `${nativeCode}_${langCode}`,
        },
      });
    } catch {
      // No usable example sentences for this chapter yet.
    }
  }

  if (!langCode || !lang) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-4xl">🌍</p>
        <p className="text-lg font-black text-slate-800">{t('grammar.noLanguage')}</p>
        <p className="text-sm text-slate-500">{t('grammar.chooseFirst')}</p>
        <button onClick={() => navigate('/')} className="rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-bold text-white">{t('grammar.goHome')}</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at 15% 0%, #ede9fe 0%, #f8fafc 50%, #ecfdf5 100%)' }}>
      <div className="mx-auto max-w-5xl space-y-7 px-4 py-10">
        <header className="flex items-center justify-between gap-4">
          <div>
            <p className="mb-1 text-xs font-black uppercase tracking-widest text-violet-600">{t('grammar.title')}</p>
            <h1 className="text-2xl font-black text-slate-900">{lang.label} {t('grammar.title')}</h1>
            <p className="mt-1 text-sm text-slate-500">{t('grammar.subtitle')}</p>
          </div>
          <button onClick={() => navigate(-1)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-500 shadow-sm">
            {t('grammar.back')}
          </button>
        </header>

        {loading && <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>}
        {error && <div className="rounded-2xl border border-slate-200 bg-white py-14 text-center shadow-sm"><p className="text-3xl">📭</p><p className="mt-3 font-bold text-slate-700">{t('grammar.notAvailable')}</p></div>}

        {!loading && !error && manifest && (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {chapters.map((chapter) => (
              <LessonCard
                key={chapter.id}
                lesson={chapter}
                onOpen={() => navigate(`/learn/grammar/${chapter.id}/lesson`)}
                onPlaySentenceBuilder={() => playSentenceBuilder(chapter)}
                sentenceBuilderLabel={t('games.sentenceBuilder')}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
